import type React from "react"
import { useState } from "react"
import styles from "./TimePicker.module.scss"

interface TimePickerProps {
  value?: { hour: string; minute: string; period: "AM" | "PM" }
  onChange: (time: { hour: string; minute: string; period: "AM" | "PM" }) => void
  className?: string
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState({ hour: false, minute: false, period: false })

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"))
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"))
  const periods = ["AM", "PM"]

  const currentTime = value || { hour: "09", minute: "00", period: "AM" as const }

  const handleSelect = (type: "hour" | "minute" | "period", selectedValue: string) => {
    const newTime = { ...currentTime }
    if (type === "period") {
      newTime.period = selectedValue as "AM" | "PM"
    } else {
      newTime[type] = selectedValue
    }
    onChange(newTime)
    setIsOpen({ ...isOpen, [type]: false })
  }

  const toggleDropdown = (type: "hour" | "minute" | "period") => {
    setIsOpen({
      hour: type === "hour" ? !isOpen.hour : false,
      minute: type === "minute" ? !isOpen.minute : false,
      period: type === "period" ? !isOpen.period : false,
    })
  }

  return (
    <div className={`${styles.container} ${className || ""}`}>
      {/* Hour Selector */}
      <div className={styles.selector}>
        <button type="button" className={styles.button} onClick={() => toggleDropdown("hour")}>
          <span>{currentTime.hour}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {isOpen.hour && (
          <div className={styles.dropdown}>
            {hours.map((hour) => (
              <button
                key={hour}
                type="button"
                className={`${styles.dropdownItem} ${hour === currentTime.hour ? styles.selected : ""}`}
                onClick={() => handleSelect("hour", hour)}
              >
                {hour}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Minute Selector */}
      <div className={styles.selector}>
        <button type="button" className={styles.button} onClick={() => toggleDropdown("minute")}>
          <span>{currentTime.minute}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {isOpen.minute && (
          <div className={styles.dropdown}>
            {minutes.map((minute) => (
              <button
                key={minute}
                type="button"
                className={`${styles.dropdownItem} ${minute === currentTime.minute ? styles.selected : ""}`}
                onClick={() => handleSelect("minute", minute)}
              >
                {minute}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Period Selector */}
      <div className={styles.selector}>
        <button type="button" className={styles.button} onClick={() => toggleDropdown("period")}>
          <span>{currentTime.period}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {isOpen.period && (
          <div className={styles.dropdown}>
            {periods.map((period) => (
              <button
                key={period}
                type="button"
                className={`${styles.dropdownItem} ${period === currentTime.period ? styles.selected : ""}`}
                onClick={() => handleSelect("period", period)}
              >
                {period}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TimePicker
